import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AccountService } from 'app/core';
import { FieldDef } from 'app/core/models';
import { LayoutService } from 'app/layout/layout.service';
import { FileFormat } from 'app/shared/enums';
import { generateDownloadableFile } from 'app/shared/functions';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { capitalizeFirstLetter } from 'app/shared/Utils/common.utils';
import { Observable, of } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import {
  CommonAction,
  LockableFieldDef,
  LockableFieldsResolverData,
  LockableFieldType,
  routeTypeMap,
} from './locked-fields.types';
import { Store } from '@ngxs/store';
import { ObjectsManagerActions } from '../objects-manager/state/actions';
import UrlUtils from '@shared/Utils/url.utils';

@Component({
  selector: 'app-locked-fields',
  templateUrl: './locked-fields.component.html',
  styleUrls: ['./locked-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LockedFieldsComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  ui = { isLoading$: of(false) };
  relatedToTypes: LockableFieldType[];
  customObjects: Partial<CustomObject>[];
  selectedType: string;

  get isLoading$(): Observable<boolean> {
    return this.ui.isLoading$;
  }

  private get isOnRootRouteLevel(): boolean {
    return this.router.url.endsWith('custom_fields');
  }

  private get typeParam(): string {
    const [, typeParam] = this.router.url.split('custom_fields/');
    return routeTypeMap[typeParam] ?? capitalizeFirstLetter(typeParam);
  }

  constructor(
    private readonly accountService: AccountService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly layoutService: LayoutService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this.handleCommonSubjects();

    const savedType = UrlUtils.getFromLocalStorage('ts_lookup_field_view', true, null);
    let hasNavigatedToSavedType = false;

    if (savedType && savedType.type) {
      this.selectedType = savedType.type;
      this.router.navigate([savedType.type.toLowerCase()], { relativeTo: this.activatedRoute });
      hasNavigatedToSavedType = true;
    }

    this.activatedRoute.data
      .pipe(untilComponentDestroyed(this))
      .subscribe(({ lockableFieldsData: { relatedToTypes, customObjects } }: LockableFieldsResolverData) => {
        this.relatedToTypes = relatedToTypes;
        this.customObjects = customObjects;

        if (this.isOnRootRouteLevel && !hasNavigatedToSavedType) {
          const [firstType] = this.relatedToTypes;
          this.selectedType = firstType.type;
          this.router.navigate([firstType.type.toLowerCase()], { relativeTo: this.activatedRoute });
        } else if (!this.isOnRootRouteLevel) {
          this.selectedType = this.typeParam;
        }
      });

    this.ui.isLoading$ = this.layoutService
      .getCommon<boolean>(CommonAction.setLoading)
      .pipe(untilComponentDestroyed(this));

    this.layoutService
      .getCommon<LockableFieldDef>(CommonAction.onFieldAdded)
      .pipe(
        untilComponentDestroyed(this),
        filter(
          (fieldDef: LockableFieldDef) =>
            !!fieldDef && !this.relatedToTypes.filter(({ type }) => type === fieldDef.related_to_type).length,
        ),
      )
      .subscribe((fieldDef: LockableFieldDef) => {
        this.relatedToTypes = [
          ...this.relatedToTypes,
          { type: fieldDef.related_to_type, canLock: false, canInherit: true },
        ];
        this.changeDetectorRef.detectChanges();
      });
  }

  private handleCommonSubjects(): void {
    this.layoutService.emitCommon(CommonAction.fieldSearch, '', 'subject');
    this.layoutService.emitCommon(CommonAction.addCustomField, '', 'subject');
    this.layoutService.emitCommon(CommonAction.importCustomField, '', 'subject');
    this.layoutService.emitCommon(CommonAction.setLoading, false, 'subject');
    this.layoutService.emitCommon(CommonAction.onlyLocked, false, 'subject');
    this.layoutService.emitCommon(CommonAction.onFieldAdded, null, 'subject');
  }

  ngOnDestroy() {
    this.store.dispatch(new ObjectsManagerActions.Get());
    super.ngOnDestroy();
  }
}
